import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styled from "styled-components";
import CTASection from "../../../components/CTASection";
import {
  GlobalContainer,
  H1,
  H2,
  Paragraph,
  LegalWrapper,
  Logo,
} from "../../../styles/AppStyles";
import { getLogo } from "../../../utils/helpers";
import { LINKS, href } from "../../../config/links";

const canonicalUrl = href(LINKS.INSURANCE.CYBER_INSURANCE_PROVIDERS);
const heroImageSrc = "/images/learning/staff-meeting.webp";

/**
 * Cyber insurance carriers and brokers that write policies for smaller organizations.
 *
 * Fields are descriptive — what kind of company it is, how you buy from it, and
 * who it targets. Coverage limits, premiums, sublimits, and claims experience are
 * deliberately excluded: they are policy-specific, negotiated per account, and
 * change constantly. The "Questions to Ask" section covers those instead.
 */
const PROVIDERS = [
  {
    name: "AIG",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "New York, New York",
    bestFit: "Midmarket",
    knownFor: "CyberEdge program with breach response and risk services",
  },
  {
    name: "At-Bay",
    type: "Insurtech carrier",
    distribution: "Through brokers",
    location: "Mountain View, California",
    bestFit: "Small business and midmarket",
    knownFor: "Bundles active security monitoring with coverage",
  },
  {
    name: "Beazley",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "London, United Kingdom",
    bestFit: "Small business and midmarket",
    knownFor: "Long-established breach response practice",
  },
  {
    name: "biBERK (Berkshire Hathaway)",
    type: "Traditional carrier",
    distribution: "Direct",
    location: "Omaha, Nebraska",
    bestFit: "Micro and small business",
    knownFor: "Direct online purchase with no broker required",
  },
  {
    name: "CNA",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "Chicago, Illinois",
    bestFit: "Small business and midmarket",
    knownFor: "CyberPrep risk-mitigation services alongside coverage",
  },
  {
    name: "Chubb",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "Philadelphia, Pennsylvania (U.S. operations)",
    bestFit: "Small business and midmarket",
    knownFor: "Cyber Enterprise Guard with 24/7 incident response",
  },
  {
    name: "Coalition",
    type: "Insurtech carrier",
    distribution: "Direct and broker",
    location: "San Francisco, California",
    bestFit: "Small business and midmarket",
    knownFor: "Pairs continuous attack-surface scanning with coverage",
  },
  {
    name: "Cowbell",
    type: "Insurtech carrier",
    distribution: "Direct and broker",
    location: "Pleasanton, California",
    bestFit: "Micro and small business",
    knownFor: "Purpose-built for smaller accounts; admitted and surplus options",
  },
  {
    name: "Embroker",
    type: "Digital broker",
    distribution: "Direct",
    location: "San Francisco, California",
    bestFit: "Small business and midmarket",
    knownFor: "Packaged programs for startups and professional services",
  },
  {
    name: "Hiscox",
    type: "Traditional carrier",
    distribution: "Direct and broker",
    location: "Hamilton, Bermuda (U.S. operations in New York)",
    bestFit: "Micro and small business",
    knownFor: "Small-business focus with online quoting",
  },
  {
    name: "NEXT Insurance",
    type: "Insurtech carrier",
    distribution: "Direct",
    location: "Palo Alto, California",
    bestFit: "Micro and small business",
    knownFor: "Fully digital purchase aimed at very small operators",
  },
  {
    name: "Nationwide",
    type: "Traditional carrier",
    distribution: "Direct and broker",
    location: "Columbus, Ohio",
    bestFit: "Small business and midmarket",
    knownFor: "Bundled small-business coverage through agents",
  },
  {
    name: "Resilience",
    type: "Insurtech carrier",
    distribution: "Through brokers",
    location: "Chicago, Illinois",
    bestFit: "Midmarket",
    knownFor: "Pairs coverage with ongoing risk-reduction engagements",
  },
  {
    name: "The Hartford",
    type: "Traditional carrier",
    distribution: "Direct and broker",
    location: "Hartford, Connecticut",
    bestFit: "Small business and midmarket",
    knownFor: "Established small-business franchise with bundled policies",
  },
  {
    name: "Thimble",
    type: "Digital broker",
    distribution: "Direct",
    location: "New York, New York",
    bestFit: "Micro and small business",
    knownFor: "Short-term and on-demand policies for very small operators",
  },
  {
    name: "Tokio Marine HCC",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "Houston, Texas",
    bestFit: "Small business and midmarket",
    knownFor: "Specialty lines including cyber for smaller accounts",
  },
  {
    name: "Travelers",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "Hartford, Connecticut",
    bestFit: "Small business and midmarket",
    knownFor: "CyberRisk program with pre-breach cybersecurity coaching",
  },
  {
    name: "Zurich North America",
    type: "Traditional carrier",
    distribution: "Through brokers",
    location: "Schaumburg, Illinois",
    bestFit: "Midmarket",
    knownFor: "Broad commercial programs with cyber as part of a package",
  },
];

const COLUMNS = [
  { key: "name", label: "Provider" },
  { key: "type", label: "Provider Type" },
  { key: "distribution", label: "How You Buy" },
  { key: "location", label: "Headquarters" },
  { key: "bestFit", label: "Best Fit For" },
  { key: "knownFor", label: "Known For" },
];

const FILTERS = [
  { key: "type", label: "Provider type" },
  { key: "distribution", label: "How you buy" },
  { key: "bestFit", label: "Best fit for" },
];

const EMPTY_FILTERS = { type: "", distribution: "", bestFit: "" };

const controlStyle = {
  padding: "0.5rem 0.6rem",
  borderRadius: "6px",
  border: "1px solid rgba(128, 128, 128, 0.45)",
  background: "transparent",
  color: "inherit",
  font: "inherit",
  fontSize: "0.9rem",
};

const primaryButtonStyle = {
  padding: "0.55rem 1rem",
  borderRadius: "6px",
  border: "1px solid #6366f1",
  background: "#6366f1",
  color: "#fff",
  font: "inherit",
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostButtonStyle = {
  ...controlStyle,
  cursor: "pointer",
  fontWeight: 600,
};

/**
 * Horizontal scroller for the comparison table.
 *
 * Needs to be a styled component rather than an inline style: forcing the
 * scrollbar to stay visible requires pseudo-element rules. Without this, macOS
 * overlay scrollbars stay hidden until the user is already scrolling, so there
 * is no cue that the table extends past the right edge.
 */
const TableScroller = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 2rem;
  padding-bottom: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: #6366f1 rgba(128, 128, 128, 0.25);

  &::-webkit-scrollbar {
    height: 12px;
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar-track {
    background: rgba(128, 128, 128, 0.25);
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #6366f1;
    border-radius: 6px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #4f46e5;
    background-clip: content-box;
  }
`;

const thStyle = {
  padding: "0.75rem",
  textAlign: "left",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "0.75rem",
  verticalAlign: "top",
  fontSize: "0.85rem",
};

/** Byte-order mark so Excel opens the export as UTF-8 rather than ANSI. */
const UTF8_BOM = String.fromCharCode(0xfeff);

/** Wrap a CSV field in quotes only when it needs them. */
const escapeCsv = (value) => {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const CyberInsuranceProviders = ({ istouch, isdarkmode }) => {
  const logoTop = getLogo(isdarkmode);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // Distinct values per filterable column, so the dropdowns stay in sync with the data.
  const filterOptions = useMemo(
    () =>
      FILTERS.reduce((acc, { key }) => {
        acc[key] = [...new Set(PROVIDERS.map((p) => p[key]))].sort((a, b) =>
          a.localeCompare(b),
        );
        return acc;
      }, {}),
    [],
  );

  const visibleProviders = useMemo(() => {
    const search = query.trim().toLowerCase();

    return PROVIDERS.filter((provider) => {
      const matchesFilters = FILTERS.every(
        ({ key }) => !filters[key] || provider[key] === filters[key],
      );
      if (!matchesFilters) return false;
      if (!search) return true;

      return COLUMNS.some(({ key }) =>
        provider[key].toLowerCase().includes(search),
      );
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [query, filters]);

  const hasActiveFilters = Boolean(
    query.trim() || FILTERS.some(({ key }) => filters[key]),
  );

  const setFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const clearFilters = () => {
    setQuery("");
    setFilters(EMPTY_FILTERS);
  };

  /** Export whatever the current filters are showing, so the file matches the screen. */
  const downloadCsv = () => {
    const rows = [
      COLUMNS.map(({ label }) => escapeCsv(label)),
      ...visibleProviders.map((provider) =>
        COLUMNS.map(({ key }) => escapeCsv(provider[key])),
      ),
    ];
    const csv = rows.map((row) => row.join(",")).join("\r\n");

    const blob = new Blob([UTF8_BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "csfsafe-cyber-insurance-providers.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <GlobalContainer $istouch={istouch} style={{ marginBottom: ".8rem" }}>
      {/* SEO / Social Meta */}
      <Helmet>
        <title>
          Cyber Insurance for Small Businesses — Provider Comparison | CSFSafe
        </title>
        <meta
          name="description"
          content="A filterable, downloadable comparison of cyber insurance carriers and brokers that write policies for small businesses — plus the coverage questions and sublimits to check before you buy."
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Preload LCP hero image */}
        <link rel="preload" as="image" href={heroImageSrc} fetchpriority="high" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Cyber Insurance for Small Businesses — Provider Comparison | CSFSafe"
        />
        <meta
          property="og:description"
          content="Compare cyber insurance carriers and brokers serving small businesses, and learn which coverage questions matter most before you buy."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="CSFSafe" />
        <meta
          property="og:image"
          content={`https://csfsafe.com${heroImageSrc}`}
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Cyber Insurance for Small Businesses — Provider Comparison | CSFSafe"
        />
        <meta
          name="twitter:description"
          content="Compare cyber insurance providers for SMBs and learn what to verify before you buy."
        />
        <meta
          name="twitter:image"
          content={`https://csfsafe.com${heroImageSrc}`}
        />

        <meta
          name="keywords"
          content="cyber insurance small business, cyber liability insurance, SMB cyber insurance, cyber insurance providers, cyber insurance comparison, ransomware insurance"
        />

        {/* FAQ schema for rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What does cyber insurance cover for a small business?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Cyber policies typically split into first-party coverage, which pays your own costs such as forensics, data restoration, business interruption, and extortion payments, and third-party coverage, which pays claims brought against you by customers or regulators. Coverage varies substantially between carriers, so the policy language matters more than the headline limit.",
                },
              },
              {
                "@type": "Question",
                name: "Does cyber insurance replace cybersecurity controls?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "No. Insurance transfers financial risk; it does not reduce the chance of an incident. Most carriers now require specific controls such as multi-factor authentication, tested backups, and endpoint detection before they will bind coverage, and misstating those controls on the application can void a claim.",
                },
              },
              {
                "@type": "Question",
                name: "What is the difference between admitted and surplus lines cyber insurance?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Admitted carriers are licensed by your state and their policies are backed by the state guaranty fund if the insurer becomes insolvent. Surplus lines carriers are not, but they have more freedom to write unusual or higher-risk accounts and to customize policy language. Ask which one you are being quoted.",
                },
              },
              {
                "@type": "Question",
                name: "Why is my social engineering coverage lower than my policy limit?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Social engineering and funds transfer fraud are usually written as sublimits, often a small fraction of the overall policy limit. A business with a $1,000,000 policy may have only $25,000 to $100,000 available for a fraudulent wire transfer. Because this is one of the most common losses small businesses actually suffer, check the sublimit rather than the headline number.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      {istouch ? (
        <Logo style={{ position: "relative", zIndex: 0, margin: "0" }}>
          <img
            src={logoTop}
            alt="CSFSafe Logo"
            style={{
              maxHeight: "60px",
              minHeight: "40px",
              objectFit: "contain",
              margin: "1rem",
            }}
          />
        </Logo>
      ) : (
        <H1 as="div">Cybersecurity Learning Center</H1>
      )}

      <LegalWrapper>
        <div
          style={{
            width: "100%",
            marginBottom: "1.5rem",
            textAlign: "center",
          }}
        >
          <img
            src={heroImageSrc}
            alt="A small business team meeting to review cyber insurance coverage"
            width={762}
            height={430}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            sizes="(min-width: 800px) 762px, 100vw"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "762px",
              borderRadius: "10px",
              objectFit: "cover",
            }}
          />
        </div>

        <H1 id="cyber-insurance-providers">
          Cyber Insurance for Small Businesses
        </H1>

        <Paragraph>
          Cyber insurance has become a practical necessity for{" "}
          <strong>small businesses</strong>, <strong>nonprofits</strong>, and{" "}
          <strong>local governments</strong>&mdash;often because a customer,
          lender, or grant program requires it. But the market is uneven. Two
          policies with the same headline limit can differ enormously in what
          they actually pay for. And <strong>74% of claims are denied</strong>.
          <br />
          <br />
          This page is a starting point for building a shortlist. Use the table
          to narrow candidates, then use the{" "}
          <a href="#questions-to-ask">questions to ask</a> to compare quotes on
          the terms that decide whether a claim gets paid.
        </Paragraph>
        <Paragraph>
          For a comprehensive guide to cyber insurance coverage, exclusions,
          costs, and why claims fail, see{" "}
          <Link to="/articles/cyber-insurance-small-business">
            Cyber Insurance for Small Businesses: Coverage, Exclusions, and Why
            Most Claims Are Denied
          </Link>
          .
        </Paragraph>

        <H2>Insurance Is Risk Transfer, Not Risk Reduction</H2>
        <Paragraph>
          This is the single most important thing to understand before buying.
          Insurance pays out <em>after</em> an incident. It does not make an
          incident less likely, and it does not restore your data, your
          reputation, or your customers&apos; trust.
        </Paragraph>
        <Paragraph>
          It also increasingly depends on controls you already have in place.
          Carriers now underwrite on your security posture, and most
          applications ask you to attest to specific controls&mdash;multi-factor
          authentication, tested backups, endpoint detection. Those answers are{" "}
          <strong>warranties</strong>. If you attest to MFA and a claim
          investigation finds it was not actually enforced, the carrier may deny
          the claim outright. Answer the application carefully and truthfully,
          and fix the gaps before you sign rather than after.
        </Paragraph>
        <Paragraph>
          Treat insurance as a complement to a{" "}
          <Link to={LINKS.CONSULTANTS.MANAGED_SERVICE_PROVIDERS}>
            managed service provider
          </Link>{" "}
          and a written cybersecurity plan&mdash;never a substitute for either.
        </Paragraph>

        <H2>Know What You Are Buying</H2>
        <Paragraph>
          The companies below are not all the same kind of business. How you buy
          affects what you can negotiate:
        </Paragraph>
        <ul>
          <li>
            <strong>Traditional carrier</strong> &mdash; an established insurer
            writing cyber as one line among many. Usually purchased through an
            agent or broker, often bundled with your other business coverage.
          </li>
          <li>
            <strong>Insurtech carrier</strong> &mdash; a technology-first
            insurer that underwrites using continuous scanning of your external
            attack surface. Many bundle security monitoring or alerting with the
            policy, which can be genuine added value for an organization with no
            internal security staff.
          </li>
          <li>
            <strong>Digital broker</strong> &mdash; not an insurer. A broker
            shops multiple carriers on your behalf and places the policy. Useful
            when you want comparison without contacting carriers individually,
            but confirm which carrier actually ends up holding the risk.
          </li>
        </ul>

        <H2 id="provider-directory">Provider Comparison</H2>
        <Paragraph
          style={{
            fontSize: "0.85rem",
            opacity: 0.75,
            borderLeft: "3px solid #ccc",
            paddingLeft: "0.75rem",
            marginBottom: "1.5rem",
          }}
        >
          <strong>Disclaimer:</strong> The companies listed below are provided
          solely as a convenience to small businesses, nonprofits, and local
          governments researching cyber coverage. CSFSafe is not an insurance
          agent, broker, or advisor, does not sell insurance, and receives no
          compensation from any company listed. Nothing here is insurance advice
          or a recommendation of any policy or carrier. Availability, appetite,
          policy terms, and pricing vary by state, industry, and account, and
          change frequently&mdash;confirm all details directly with the carrier
          or a licensed broker. Organizations should conduct their own due
          diligence and consult a licensed professional before purchasing
          coverage.
        </Paragraph>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            alignItems: "flex-end",
            marginBottom: "1rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
            <label
              htmlFor="insurer-search"
              style={{ fontSize: "0.8rem", fontWeight: 600, opacity: 0.8 }}
            >
              Search
            </label>
            <input
              id="insurer-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, location, focus…"
              style={{ ...controlStyle, minWidth: "220px" }}
            />
          </div>

          {FILTERS.map(({ key, label }) => (
            <div
              key={key}
              style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}
            >
              <label
                htmlFor={`insurer-filter-${key}`}
                style={{ fontSize: "0.8rem", fontWeight: 600, opacity: 0.8 }}
              >
                {label}
              </label>
              <select
                id={`insurer-filter-${key}`}
                value={filters[key]}
                onChange={(e) => setFilter(key, e.target.value)}
                style={{ ...controlStyle, minWidth: "190px" }}
              >
                <option value="">All</option>
                {filterOptions[key].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <button type="button" onClick={downloadCsv} style={primaryButtonStyle}>
            Download CSV
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              style={ghostButtonStyle}
            >
              Clear filters
            </button>
          )}
        </div>

        <Paragraph
          aria-live="polite"
          style={{ fontSize: "0.85rem", opacity: 0.75, marginBottom: "0.75rem" }}
        >
          Showing {visibleProviders.length} of {PROVIDERS.length} providers.
          {hasActiveFilters && " The CSV download matches your current filters."}
        </Paragraph>

        <TableScroller>
          <table
            style={{
              width: "100%",
              minWidth: "860px",
              borderCollapse: "collapse",
              fontSize: "0.9rem",
              lineHeight: "1.4",
            }}
          >
            <caption
              style={{
                captionSide: "bottom",
                padding: "0.75rem 0",
                fontSize: "0.8rem",
                opacity: 0.7,
                textAlign: "left",
              }}
            >
              Cyber insurance carriers and brokers serving smaller
              organizations, sorted alphabetically. Scroll horizontally to see
              all columns.
            </caption>
            <thead>
              <tr
                style={{
                  backgroundColor: "rgba(99, 102, 241, 0.1)",
                  borderBottom: "2px solid #6366f1",
                }}
              >
                {COLUMNS.map(({ key, label }) => (
                  <th key={key} scope="col" style={thStyle}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleProviders.map((provider) => (
                <tr
                  key={provider.name}
                  style={{ borderBottom: "1px solid rgba(128,128,128,0.25)" }}
                >
                  <th
                    scope="row"
                    style={{ ...tdStyle, textAlign: "left", fontSize: "0.9rem" }}
                  >
                    {provider.name}
                  </th>
                  {COLUMNS.slice(1).map(({ key }) => (
                    <td key={key} style={tdStyle}>
                      {provider[key]}
                    </td>
                  ))}
                </tr>
              ))}
              {visibleProviders.length === 0 && (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    style={{ ...tdStyle, textAlign: "center", opacity: 0.75 }}
                  >
                    No providers match these filters. Try clearing one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableScroller>

        <H2 id="questions-to-ask">Questions to Ask Before You Buy</H2>
        <Paragraph>
          Comparing cyber quotes on price and headline limit alone is how
          organizations end up underinsured for the loss they actually suffer.
          Ask these, and get the answers from the <strong>policy wording</strong>
          &mdash;not the quote summary.
        </Paragraph>
        <ul>
          <li>
            <strong>What are the sublimits?</strong> This is the biggest
            practical gap. Social engineering and funds transfer fraud are
            usually sublimited far below the policy limit&mdash;a $1M policy may
            carry only $25K&ndash;$100K for a fraudulent wire. That is the loss
            small businesses most often actually experience.
          </li>
          <li>
            <strong>Is ransomware and extortion covered, and at what limit?</strong>{" "}
            Confirm whether ransom payments, negotiation, and the recovery work
            afterward are all covered, and whether extortion carries its own
            sublimit.
          </li>
          <li>
            <strong>Admitted or surplus lines?</strong> Admitted carriers are
            state-licensed and backed by the state guaranty fund if the insurer
            fails. Surplus lines are not, but offer more flexible terms. Know
            which you are buying.
          </li>
          <li>
            <strong>What is the business interruption waiting period?</strong>{" "}
            Many policies do not pay for the first 8&ndash;12 hours of downtime.
            If your outages typically resolve inside that window, the coverage
            may never trigger.
          </li>
          <li>
            <strong>Who handles the breach response?</strong> Most carriers
            require you to use their panel of forensics and legal vendors. Ask
            whether you can use your own MSP or counsel, and whether using them
            reduces or voids reimbursement.
          </li>
          <li>
            <strong>What controls are required to bind and keep coverage?</strong>{" "}
            Get the list in writing. Treat every control you attest to as a
            warranty you must actually maintain for the full policy term.
          </li>
          <li>
            <strong>How does the war and nation-state exclusion read?</strong>{" "}
            Carriers have tightened this considerably. Given that
            state-sponsored actors now routinely hit ordinary infrastructure,
            ask specifically how the exclusion would apply to an attack later
            attributed to a foreign government.
          </li>
          <li>
            <strong>Claims-made or occurrence, and what is the retroactive date?</strong>{" "}
            Most cyber policies are claims-made. A breach that began before the
            retroactive date may not be covered even if you discover it while
            insured.
          </li>
          <li>
            <strong>Is there a dependent business interruption clause?</strong>{" "}
            If your MSP, payment processor, or key SaaS vendor goes down and
            takes you with it, standard business interruption may not respond.
          </li>
        </ul>

        <H2>Strengthen Your Position Before You Apply</H2>
        <Paragraph>
          The controls carriers ask about on the application are the same ones
          that reduce your risk of needing to claim: enforced multi-factor
          authentication, tested and isolated backups, endpoint detection,
          patching discipline, and a written incident response plan.
          Organizations that can document these honestly get better terms and
          fewer coverage disputes. Building that documentation before you
          shop&mdash;rather than scrambling to answer an application&mdash;is
          the highest-leverage thing you can do.
        </Paragraph>

        <CTASection />

        <H2>What's Changing in 2026: AI Exclusions and New Requirements</H2>
        <Paragraph>
          The cyber insurance market shifted significantly in 2026. Insurers are
          adding new exclusions and requirements that directly affect your
          coverage:
        </Paragraph>
        <ul>
          <li>
            <strong>AI exclusions are now standard.</strong> 42% of companies
            report AI-related exclusions in their cyber policies. Insurers are
            excluding losses related to shadow AI (unauthorized AI tools) and
            autonomous AI agents. If your organization uses AI tools, ask your
            carrier explicitly whether the policy covers losses involving AI.
          </li>
          <li>
            <strong>EDR/XDR is increasingly required.</strong> Carriers now
            require endpoint detection and response (EDR) or extended detection
            and response (XDR) monitoring as a condition of coverage, often with
            proof of 24/7 monitoring by a Security Operations Center.
          </li>
          <li>
            <strong>Premium increases are closing the window.</strong> S&P
            forecasts 15–20% increases in premiums in 2026. If you are
            considering renewal or new coverage, locking in rates now is
            advantageous.
          </li>
          <li>
            <strong>AI security credits are available.</strong> Conversely, 80%
            of companies using AI-powered defenses now receive premium credits
            and reductions. Demonstrating active, defended use of AI for
            security can lower your costs.
          </li>
        </ul>

        <Paragraph className="last-updated">
          Last updated: August 31, 2026
        </Paragraph>
      </LegalWrapper>
    </GlobalContainer>
  );
};

CyberInsuranceProviders.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default CyberInsuranceProviders;
